import { ActionMobilesLookupResponse, AuthzRequestMobilesLookup, PublicResponse } from '@afterpay/verify-service-api'
import { SupportedLocale } from '@afterpaytouch/shop-api'
import { CookieActiveGroup, getOnetrustCookieConsent, onOnetrustCookieConsentChange } from '../../integrations/oneTrust'
import { setCookie, destroyCookie } from 'nookies'
import { unwrapResult } from '@reduxjs/toolkit'
import {
  AsyncAction,
  checkCrossBorder,
  checkEmailEligibility,
  fingerprintSelector,
  portalAction,
  PortalThunk,
  profilingIdSelector,
  tokenSelector,
  countrySelector,
  mobileLoginSelector,
  getConsumerTagsWithData,
  verifyServiceClient,
} from '../'
import { UserLookupResponse } from '@afterpaytouch/portal-api/consumer/lookup/types'
import {
  ConfirmVerifyAccountMobile,
  ConsumerAccountResponse,
  EmailLookupRequest,
  HandleUserLookupResult,
  UpdateAccountDetails,
  UpdateAccountDetailsRequest,
  VerifyAccountMobileRequest,
  APT_USER_ID,
  APT_USER_COUNTRY,
  APT_USERNAME,
  APT_PRELOGIN_USER_ID,
  TrackingEvent,
  isPublicResponse,
  CheckoutError,
} from '../../model'
import { preferredLocale } from './hooks'
import { isUndefinedOrEmpty } from '@afterpay/utils/string'
import { encrypt, isValidEmail } from '@afterpay/utils'
import { getDomain } from '../../utils/browser'
import { FEDERATION_OUTBOX_KEY } from '../../integrations/userJourney/constants'
import { Country } from '@afterpay/types'
import { ConsumerAccountIdentitiesResponse } from '@afterpaytouch/portal-api/consumer/account/types'
import { getLocalStorageItem, setLocalStorageItem } from '@afterpay/utils/storage'
import { logCustomMonitoringEvent } from '../../integrations/monitoring'
import { isCookieEnabled } from '../convergence/helper'

export const handleEmailLookup =
  (email: string, isFromRegistration: boolean = false, skipCrossRegionCheck: boolean = false): AsyncAction<HandleUserLookupResult> =>
  async (dispatch) => {
    const [lookupRes] = await Promise.all([dispatch(emailLookup({ email, isFromRegistration })), dispatch(checkEmailEligibility(email))])
    const lookup = unwrapResult(lookupRes)
    if (lookup.existing && !skipCrossRegionCheck) {
      const crossRegionCheck = await dispatch(checkCrossBorder(lookup.countryCode, email))
      if (crossRegionCheck !== null) {
        return crossRegionCheck
      }
    }
    return lookup
  }

export interface CheckoutActionLookupResponse extends ActionMobilesLookupResponse {
  isFromInitialiseCheckout: boolean
}

export interface PublicResponseWithSource extends PublicResponse {
  source: 'authz' | 'action'
}

export const getErrorFromPublicResponseWithSource = (response: PublicResponseWithSource): CheckoutError => {
  const error = ['mobiles_lookup', response.source, response.code].join('_').toUpperCase()
  return { error }
}

export const mobileLookup: PortalThunk<{ mobile: string; isFromInitialiseCheckout: boolean }, CheckoutActionLookupResponse | PublicResponseWithSource> =
  portalAction('consumer/mobile', async ({ mobile, isFromInitialiseCheckout }, thunk) => {
    const deviceFingerprint = JSON.stringify(fingerprintSelector(thunk.getState()))
    const profilingSessionId = profilingIdSelector(thunk.getState())
    const transactionToken = tokenSelector(thunk.getState())
    let amplitudeSessionId

    try {
      amplitudeSessionId = (await import('amplitude-js')).default.getInstance()?.getSessionId()
    } catch (e) {
      console.warn('amplitude failed to load dynamically', e)
    }

    const request: AuthzRequestMobilesLookup = {
      mobile,
      transactionToken,
      amplitudeSessionId,
      profilingSessionId,
      deviceFingerprint,
    }

    const authzResponse: PublicResponse = await verifyServiceClient.authzMobilesLookup(request)
    if (authzResponse.code === 'FAILED') {
      return { ...authzResponse, source: 'authz' }
    }

    const actionResponse = await verifyServiceClient.actionMobilesLookup(transactionToken)
    if (isPublicResponse(actionResponse)) {
      return { ...actionResponse, source: 'action' }
    }
    return { ...actionResponse, isFromInitialiseCheckout }
  })

export const emailLookup: PortalThunk<{ email: string; isFromRegistration: boolean }, UserLookupResponse> = portalAction(
  'consumer/email',
  async ({ email }, thunk) => {
    const transactionToken = tokenSelector(thunk.getState())
    let amplitudeSessionId

    try {
      amplitudeSessionId = (await import('amplitude-js')).default.getInstance()?.getSessionId()
    } catch (e) {
      console.warn('amplitude failed to load dynamically', e)
    }

    const request: EmailLookupRequest = {
      email,
      transactionToken,
      amplitudeSessionId,
    }

    return await thunk.extra.portalApi().post('/portal/consumers/emails/lookup', request)
  }
)

const clearLocalStorageExceptPersistent = (): void => {
  const biometricLocalStorage = getLocalStorageItem('apt-biometric-settings') ?? false
  const federationOutbox = getLocalStorageItem(FEDERATION_OUTBOX_KEY)
  localStorage.clear()
  if (biometricLocalStorage) {
    setLocalStorageItem('apt-biometric-settings', biometricLocalStorage)
  }
  if (federationOutbox !== null) {
    setLocalStorageItem(FEDERATION_OUTBOX_KEY, federationOutbox)
  }
}

const handleSetUUIDInStorage = (consumerUuid): void => {
  const canRetrieveCookies = isCookieEnabled()

  if (canRetrieveCookies) {
    return
  }
  try {
    clearLocalStorageExceptPersistent()
    setLocalStorageItem(APT_USER_ID, consumerUuid)
  } catch (e) {
    logCustomMonitoringEvent(TrackingEvent.SET_UUID_LOCAL_STORAGE_FAILED, {
      errorName: e.name,
      errorCode: e.code,
    })
  }
}

const setPostLoginCookies = (consumerPreferredLocale: SupportedLocale, consumerUuid: string, country: Country): void => {
  if (!isUndefinedOrEmpty(consumerPreferredLocale)) {
    setCookie(null, 'NEXT_LOCALE', consumerPreferredLocale, {
      path: '/',
      sameSite: 'none',
      secure: true,
    })
  }
  if (!isUndefinedOrEmpty(consumerUuid)) {
    setCookie(null, APT_USER_ID, consumerUuid, {
      path: '/',
      sameSite: 'none',
      secure: true,
      maxAge: 31536000,
      domain: getDomain(),
    })

    handleSetUUIDInStorage(consumerUuid)

    destroyCookie(null, APT_PRELOGIN_USER_ID, {
      path: '/',
    })
  }

  if (!isUndefinedOrEmpty(country)) {
    setCookie(null, APT_USER_COUNTRY, country, {
      path: '/',
      sameSite: 'none',
      secure: true,
    })
  }
}

export const getIdentities: PortalThunk<void, ConsumerAccountIdentitiesResponse> = portalAction('consumer/account/identities', async (request, thunk) => {
  return await thunk.extra.portalApi().get<ConsumerAccountIdentitiesResponse>('/portal/consumers/account/identities')
})

export const accountLookup: PortalThunk<void, ConsumerAccountResponse> = portalAction('consumer/account', async (request, thunk) => {
  const response = await thunk.extra.portalApi().get<ConsumerAccountResponse>('/portal/consumers/account')
  const consumerPreferredLocale = response?.preferredLocale
  const consumerUuid = response?.uuid
  const consumerCountry = countrySelector(thunk.getState())
  const isMarketingCookieConsent = getOnetrustCookieConsent(consumerCountry, CookieActiveGroup.Marketing)
  const { isMobileLogin } = mobileLoginSelector(thunk.getState())

  await thunk.dispatch(getConsumerTagsWithData.initiate(undefined, { forceRefetch: true }))

  const hasValidEmail = isValidEmail(response?.email)

  if (response?.profileConfirmed) {
    if (!isMobileLogin && !hasValidEmail) {
      destroyCookie(null, APT_USERNAME, {
        path: '/',
      })
    } else {
      setCookie(null, APT_USERNAME, JSON.stringify(encrypt(isMobileLogin ? response?.phoneNumber ?? response?.email : response?.email)), {
        path: '/',
        sameSite: 'none',
        secure: true,
        maxAge: 31536000,
      })
    }
  }

  if (isMarketingCookieConsent) {
    setPostLoginCookies(consumerPreferredLocale, consumerUuid, consumerCountry)
  } else {
    onOnetrustCookieConsentChange(consumerCountry, CookieActiveGroup.Marketing, () =>
      setPostLoginCookies(consumerPreferredLocale, consumerUuid, consumerCountry)
    )
  }

  return response
})

export const verifyAccountMobile: PortalThunk<VerifyAccountMobileRequest, void> = portalAction(
  'consumer/account/verifyAccountMobile',
  async (request, thunk) => {
    return await thunk.extra.portalApi().post('/portal/consumers/account/sms-verification', request)
  }
)

export const confirmVerifyAccountMobile: PortalThunk<ConfirmVerifyAccountMobile, void> = portalAction(
  'consumer/account/confirmVerifyAccountMobile',
  async (request, thunk) => {
    return await thunk.extra.portalApi().post('/portal/consumers/account/mobile', request)
  }
)

export const updateAccountDetails: PortalThunk<UpdateAccountDetails, void> = portalAction(
  'consumer/account/updateAccountDetails',
  async (accountDetails, thunk) => {
    const locale = preferredLocale(thunk.getState())
    const request: UpdateAccountDetailsRequest = {
      ...accountDetails,
      preferredLocale: locale,
      creditPolicyAgree: false,
    }
    return await thunk.extra.portalApi().put('/portal/consumers/account/update-account-details', request)
  }
)

export const accountKycConfirmDetails: PortalThunk<void, void> = portalAction('consumer/account/kycRefresh', async (_, thunk) => {
  return await thunk.extra.portalApi().put('/portal/consumers/account/profile-refresh', {})
})
